/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";

import { resetAuth } from "./slices/authSlice";
import { RootState } from "@/types/General";
import { API_URL, END_POINTS, NODE_API_URL } from "@/constants/url";
import { getFromStorage } from "@/constants/storage";
import { STORAGE_KEYS } from "@/constants/storageKeys";

/* -------------------------------------------------------------------------- */
/*                               SHARED HEADERS                               */
/* -------------------------------------------------------------------------- */

const prepareHeadersShared = (
  headers: Headers,
  { getState }: { getState: () => unknown }
) => {
  const { token } = (getState() as RootState).auth;
  const { tempToken } = (getState() as RootState).auth;
  const tokenFromStorage = getFromStorage(STORAGE_KEYS.token);
  const nodeToken = getFromStorage(STORAGE_KEYS.tokenNode);

  let tokenKey;
  if (token) {
    tokenKey = token;
  } else if (tempToken) {
    tokenKey = tempToken;
  } else {
    tokenKey = tokenFromStorage;
  }

  if (token || tempToken || tokenFromStorage) {
    headers.set("authorization", `Bearer ${tokenKey}`);
  }

  if (nodeToken) {
    headers.set("AuthorizationNode", nodeToken);
  }

  headers.set("x-portal", "user");
  headers.set("app", "anstmasr2588");

  return headers;
};

/* -------------------------------------------------------------------------- */
/*                               BASE QUERIES                                  */
/* -------------------------------------------------------------------------- */

const baseQueryApi = fetchBaseQuery({
  baseUrl: API_URL.replace(/\/$/, ""), // 🔒 remove trailing slash
  prepareHeaders: prepareHeadersShared,
});

const baseQueryNode = fetchBaseQuery({
  baseUrl: NODE_API_URL.replace(/\/$/, ""), // 🔒 remove trailing slash
  prepareHeaders: prepareHeadersShared,
});

const HOSTS = {
  api: baseQueryApi,
  node: baseQueryNode,
} as const;

/* -------------------------------------------------------------------------- */
/*                        DYNAMIC BASE QUERY WRAPPER                           */
/* -------------------------------------------------------------------------- */

const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const rawUrl = typeof args === "string" ? args : args.url;

  if (!rawUrl) {
    // console.error("❌ RTK Query Error: URL is missing");
    return {
      error: {
        status: "CUSTOM_ERROR",
        data: "URL is missing",
      } as any,
    };
  }

  /**
   * Expected format:
   *  - api:/login
   *  - node:/register
   */
  const match = rawUrl.match(/^([a-z]+):\/(.+)$/i);

  const hostKey = (match?.[1]?.toLowerCase() as keyof typeof HOSTS) || "api";
  const path = match ? `/${match[2]}` : rawUrl;

  const selectedBaseQuery = HOSTS[hostKey] || HOSTS.api;

  const adjustedArgs: FetchArgs =
    typeof args === "string"
      ? { url: path }
      : {
        ...args,
        url: path,
        // Ensure Content-Type is set for POST requests with body
        ...(args.method === "POST" && args.body && typeof args.body === "object" && !(args.body instanceof FormData)
          ? {
            headers: {
              "Content-Type": "application/json",
              ...(args.headers || {}),
            },
          }
          : {}),
      };

  /* ------------------------------- LOG REQUEST ------------------------------ */

  console.log("🚀 API REQUEST");
  console.log("➡️ Host:", hostKey.toUpperCase());
  console.log("➡️ URL:", path);
  console.log("➡️ Method:", adjustedArgs.method || "GET");
  if (adjustedArgs.body) {
    console.log("➡️ Body:", adjustedArgs.body);
  }
  if (adjustedArgs.headers) {
    console.log("➡️ Headers:", adjustedArgs.headers);
  }
  console.log("⏰", new Date().toISOString());

  /* ----------------------------- EXECUTE REQUEST ----------------------------- */

  const result = await selectedBaseQuery(adjustedArgs, api, extraOptions);

  /* ------------------------------ LOG RESPONSE ------------------------------- */

  if (result.error) {
    // console.error("❌ API ERROR");
    // console.error("➡️ Status:", result.error.status);
    // console.error("➡️ Data:", result.error.data);
  } else {
    // console.log("✅ API SUCCESS");
    // console.log("➡️ Data:", result.data);
  }

  /* ----------------------------- GLOBAL HANDLING ----------------------------- */

  if (result.error?.status === 401) {
    api.dispatch(resetAuth());
    if (typeof window !== "undefined") {
      // Get current pathname to avoid redirect loops
      const currentPath = window.location.pathname;
      
      // Skip redirect if already on auth pages or if the API call is for login/register
      const isAuthPage = currentPath.startsWith("/auth/");
      const isLoginEndpoint = path.includes("/login") || path.includes("/register") || path.includes("/verifyOtp");
      
      // Only redirect if not already on auth page and not calling login-related endpoints
      if (!isAuthPage && !isLoginEndpoint) {
        window.location.href = "/auth/login";
      }
    }
  }

  if (result.error?.status === 503) {
    if (typeof window !== "undefined") {
      window.location.href = "/maintenance";
    }
  }

  if (result.error?.status === 500) {
    if (typeof window !== "undefined") {
      // Get current pathname to avoid redirect loops
      const currentPath = window.location.pathname;
      const isAuthPage = currentPath.startsWith("/auth/");
      const isLoginEndpoint = path.includes("/login") || path.includes("/register");
      
      // Only redirect if not already on auth page and not calling login-related endpoints
      if (!isAuthPage && !isLoginEndpoint) {
        // Redirect to login when server error occurs (fallback instead of 404 internalServerError)
        window.location.href = "/auth/login";
      }
    }
  }

  return result;
};

/* -------------------------------------------------------------------------- */
/*                                  API SLICE                                  */
/* -------------------------------------------------------------------------- */

const emptySplitApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["DASHBOARD", "PROFILE", "CATEGORY", "CART", "ORDERS", "ADDRESS", "WISHLIST", "PREFERENCES", "WALLET", "REFERRAL"],
  endpoints: (builder) => ({
    getReferralStats: builder.query<any, void>({
      query: () => ({ url: END_POINTS.getReferralStats, method: "GET" }),
      providesTags: ["REFERRAL"],
    }),
    getReferralHistory: builder.query<any, void>({
      query: () => ({ url: END_POINTS.getReferralHistory, method: "GET" }),
      providesTags: ["REFERRAL"],
    }),
  }),
});

export const { useGetReferralStatsQuery, useGetReferralHistoryQuery } = emptySplitApi;
export default emptySplitApi;
